import { Component, OnInit } from '@angular/core';
import { CommonModule } from '@angular/common';
import { FormsModule } from '@angular/forms';
import { HttpClientModule } from '@angular/common/http';
import { MatCardModule } from '@angular/material/card';
import { MatIconModule } from '@angular/material/icon';
import { MatButtonModule } from '@angular/material/button';
import { MatMenuModule } from '@angular/material/menu';
import { MatFormFieldModule } from '@angular/material/form-field';
import { MatInputModule } from '@angular/material/input';
import { MatSelectModule } from '@angular/material/select';
import { MatExpansionModule } from '@angular/material/expansion';
import { MatListModule } from '@angular/material/list';
import { MatProgressSpinnerModule } from '@angular/material/progress-spinner';
import { CourseService } from '../services/course.service';
import { KeycloakAuthService } from '../services/keycloak-auth.service';
import { Course } from '../models/courseModels';

interface CourseStat {
  title: string;
  summaryLabel: string;
  summaryValue: string;
  periodLabel: string;
  changePercentage: number;
  changeDirection: 'increase' | 'decrease';
  chartData: any;
}

interface TopPerformingCoursesStat {
  title: string;
  topCourses: any[];
  periodLabel: string;
  lineChartData: any;
}

@Component({
  selector: 'app-course',
  standalone: true,
  imports: [
    CommonModule,
    FormsModule,
    HttpClientModule,
    MatCardModule,
    MatIconModule,
    MatButtonModule,
    MatMenuModule,
    MatFormFieldModule,
    MatInputModule,
    MatSelectModule,
    MatExpansionModule,
    MatListModule,
    MatProgressSpinnerModule
  ],
  templateUrl: './course.component.html',
  styleUrl: './course.component.scss'
})
export class CourseComponent implements OnInit {
  // Mock stats data (unchanged, as no API provides this yet)
  activeCohortsStat: CourseStat = { 
    title: 'Total Active Cohorts', 
    summaryLabel: 'Active courses', 
    summaryValue: '...', 
    periodLabel: 'Last 30 days', 
    changePercentage: 0, 
    changeDirection: 'increase', 
    chartData: {} 
  };
  sessionsCompleteStat: CourseStat = { 
    title: 'Total Sessions Complete', 
    summaryLabel: 'Sessions', 
    summaryValue: '...', 
    periodLabel: 'Last 30 days', 
    changePercentage: 0, 
    changeDirection: 'increase', 
    chartData: {} 
  };
  engagementRateStat: CourseStat = { 
    title: 'Engagement Rate', 
    summaryLabel: 'Average engagement rate', 
    summaryValue: '...', 
    periodLabel: 'Last 30 days', 
    changePercentage: 0, 
    changeDirection: 'increase', 
    chartData: {} 
  };
  topPerformingStat: TopPerformingCoursesStat = { 
    title: 'Top Performing Courses', 
    topCourses: [], 
    periodLabel: 'Last 30 days', 
    lineChartData: {} 
  };

  // Course data fetched from API
  courses: Course[] = [];
  isLoading: boolean = false;
  error: string | null = null;
  selectedBot: string = 'lwazi'; // Default bot
  availableBots: string[] = ['lwazi', 'lesedi', 'kitso']; // Available bots

  panelOpenState: boolean = false;

  constructor(
    private courseService: CourseService, 
    private keycloakService: KeycloakAuthService
  ) { }

  ngOnInit(): void {
    this.keycloakService.currentUser$.subscribe(user => {
      if (user) {
        this.loadCourses();
      }
    });
  }

  loadCourses(bot: string = this.selectedBot, type: string = 'path'): void {
    this.isLoading = true;
    this.error = null;
    this.courses = [];

    this.courseService.getCoursesByBot(bot, type).subscribe({ 
      next: (courses) => {
        this.courses = courses;
        this.isLoading = false;
      },
      error: (err) => {
        console.error('Failed to load courses:', err);
        this.error = `Failed to load courses for ${bot}. ${err.message || 'Please try again later.'}`;
        this.isLoading = false;
      }
    });
  }

  // Handle bot selection change
  onBotChange(bot: string): void {
    this.selectedBot = bot;
    this.loadCourses(bot);
  }

  // Helper functions
  getChangeIcon(direction: 'increase' | 'decrease'): string {
    return direction === 'increase' ? 'arrow_upward' : 'arrow_downward';
  }

  getChangeClass(direction: 'increase' | 'decrease'): string {
    return direction === 'increase' ? 'change-increase' : 'change-decrease';
  }

  getStatusClass(enabled: boolean): string {
    return enabled ? 'status-active' : 'status-inactive';
  }

  getStatusText(enabled: boolean): string {
    return enabled ? 'Active' : 'Inactive';
  }

  // Placeholder for bot status (not used in current API)
  getBotStatusClass(status: 'Linked' | 'Not Linked'): string {
    return status === 'Linked' ? 'status-linked' : 'status-not-linked';
  }

  editCourse(courseId: string, event: MouseEvent): void {
    event.stopPropagation();
    // Navigation/modal logic here (unchanged)
  }

  createNewCourse(): void {
    // Logic to navigate/open modal (unchanged)
  }
}